import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Select,
  Upload,
  message,
  Checkbox,
  Spin,
  Typography,
  Row,
  Col,
  Input,
  Grid,
  Card,
  Tag,
  Tooltip,
} from "antd";
import {
  CheckCircleOutlined,
  CheckCircleFilled,
  CloudUploadOutlined,
  InfoCircleOutlined,
  CopyOutlined,
  TagOutlined,
  FileTextOutlined,
  AppstoreOutlined,
  SendOutlined,
  SaveOutlined,
  DeleteOutlined,
  LoginOutlined,
  LogoutOutlined,
  CloudServerOutlined,
  HistoryOutlined,
  EditOutlined,
} from "@ant-design/icons";
import {
  get_model,
  submit_model,
  get_SubmissionDraft,
  submit_draft,
  submit_draft_delete,
  get_getLimitSubmission,
  get_tutorial,
} from "../../../services/submission";
import { post_uploadResult } from "../../../services/uploads";
import { useAuth } from "../../../utils/Auth/AuthWrapper";
import { useTranslation } from "react-i18next";
import { setting_all } from "../../../services/setting";
import moment from "moment";
import "../styles/index.less";

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

interface SubmissionModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
  taskId: string;
  taskName: string;
  taskDetail: any;
  userCode: string;
  isTaskAdmin?: boolean;
}

const SubmissionModal: React.FC<SubmissionModalProps> = ({
  visible,
  onClose,
  onSubmitSuccess,
  taskId,
  taskName,
  taskDetail,
  userCode,
  isTaskAdmin,
}) => {
  const [form] = Form.useForm();
  const auth = useAuth();
  const { t } = useTranslation();
  const screens = useBreakpoint();

  const [model, setModel] = useState("");
  const [selectedModel, setSelectedModel] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pathUploadFile, setPathUploadFile] = useState<any>("");
  const [disabledSubmiss, setDisabledSubmiss] = useState(true);
  const [dataset, setDataset] = useState<any>([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [dataFile, setDataFile] = useState<any>();
  const [subType, setSubType] = useState<any>(1);
  const [submissionCount, setSubmissionCount] = useState<number>(0);
  const [submissionLimitInfo, setSubmissionLimitInfo] = useState<{ limit: number; typeDate: number } | null>(null);
  const [tutorial, setTutorial] = useState({
    login: "",
    logout: "",
    image_pull: "",
    image_push: "",
    image_tag: "",
  });
  const [editableTutorial, setEditableTutorial] = useState({
    image_tag: "",
    image_push: "",
  });
  const [editingField, setEditingField] = useState<{
    image_tag: boolean;
    image_push: boolean;
  }>({ image_tag: false, image_push: false });

  const isMobile = !screens.md;

  useEffect(() => {
    if (uploadSuccess && pathUploadFile) {
      form.validateFields(["file"]);
    }
  }, [uploadSuccess, pathUploadFile]);

  useEffect(() => {
    if (visible) {
      getModel();
      getDraft();
      getSubmission(taskDetail);
      setupDataset();
      getTutorialData();
    } else {
      form.resetFields();
      setModel("");
      setUploadSuccess(false);
      setDataFile(undefined);
      setPathUploadFile("");
      setIsLoading(false);
    }
  }, [visible, taskDetail]);

  const setupDataset = () => {
    if (taskDetail && taskDetail.dataset) {
      const datasetParsed = JSON.parse(taskDetail.dataset).dataset;
      const hasPublic =
        datasetParsed?.public && datasetParsed.public.length > 0;
      const hasPrivate =
        datasetParsed?.private && datasetParsed.private.length > 0;

      const datasetToUse = hasPrivate
        ? datasetParsed.private
        : hasPublic
          ? datasetParsed.public
          : [];

      const datasetOptions = datasetToUse.map((item: any, index: number) => ({
        ...item,
        label: item.desciption,
        value: index,
      }));

      setDataset(datasetOptions);
      form.setFieldValue("dataset", datasetOptions.map((item: any) => item.value));
    }
    setSubType(taskDetail?.submission_type || 1);
  };

  const getTutorialData = async () => {
    try {
      const res = await get_tutorial({ task_id: taskId });
      setTutorial(res);
      setEditableTutorial({
        image_tag: res.image_tag,
        image_push: res.image_push,
      });
      setEditingField({ image_tag: false, image_push: false });
    } catch (error) {
      console.error(error);
    }
  };

  const getModel = async () => {
    try {
      const res = await get_model({
        task_id: taskId,
        user_code: userCode,
      });
      let model_list = [];
      for (let a in res.images) {
        // let name = res.images[a].image.toString().split(":");
        let name = res.images[a].image.toString().split("/");
        let item = {
          label: name[2],
          value: res.images[a].image + "+" + res.images[a].digest,
        };
        model_list.push(item);
      }
      setSelectedModel(model_list);
    } catch (error) {
      console.error(error);
    }
  };

  const getDraft = async () => {
    try {
      const data = { task_id: taskId };
      const res = await get_SubmissionDraft(data);
      if (res.status === "success") {
        const subDraft = res.data[0];
        form.setFieldsValue({
          file: subDraft?.docker_image,
          model: subDraft?.docker_image
            ? subDraft?.docker_image + "+" + subDraft?.docker_id
            : null,
          ...(res.data[0]?.dataset[0] && {
            dataset: [Number(JSON.parse(res.data[0]?.dataset)[0]?.value)],
          }),
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const calcSubmissionWindow = (
    submissionTypeDate: number,
    submissionTimeConfig: any,
    defaultTime: string,
  ): { start_date: string; end_date: string } | null => {
    const now = moment();
    const fmt = "YYYY-MM-DD HH:mm:ss";

    // typedate 0 = all time, no date filter
    if (submissionTypeDate === 0) return null;

    const rawTime = submissionTimeConfig?.time || defaultTime;
    const [hourStr, minuteStr] = rawTime.split(":");
    const hour = Number(hourStr);
    const minute = Number(minuteStr);

    if (submissionTypeDate === 1) {
      // Per day: cron = "mm HH * * *"
      const todayReset = moment()
        .hour(hour)
        .minute(minute)
        .second(0)
        .millisecond(0);
      const prevDay = now.isBefore(todayReset)
        ? todayReset.clone().subtract(1, "days")
        : todayReset.clone();
      return {
        start_date: prevDay.format(fmt),
        end_date: prevDay.clone().add(1, "days").format(fmt),
      };
    }

    if (submissionTypeDate === 2) {
      // Per week: cron = "mm HH * * D" (D: 0=Sun, 1=Mon, ..., 6=Sat)
      const dayOfWeek = Number(submissionTimeConfig?.day ?? 1);
      let prevReset = moment()
        .day(dayOfWeek)
        .hour(hour)
        .minute(minute)
        .second(0)
        .millisecond(0);
      while (prevReset.isAfter(now)) prevReset.subtract(7, "days");
      return {
        start_date: prevReset.format(fmt),
        end_date: prevReset.clone().add(7, "days").format(fmt),
      };
    }

    if (submissionTypeDate === 3) {
      // Per month: reset on specific day of month at specific time
      // date format: "DD-MM-YYYY" (only day part used)
      const dateParts = (submissionTimeConfig?.date || "")
        .split("-")
        .map(Number);
      const dayOfMonth = dateParts[0] || 1;
      let prevReset = moment()
        .date(dayOfMonth)
        .hour(hour)
        .minute(minute)
        .second(0)
        .millisecond(0);
      if (now.isBefore(prevReset)) prevReset.subtract(1, "month");
      return {
        start_date: prevReset.format(fmt),
        end_date: prevReset.clone().add(1, "month").format(fmt),
      };
    }

    return null;
  };

  const getSubmission = async (task_detail: any) => {
    if (task_detail?.submission_limit) {
      setSubmissionLimitInfo({
        limit: task_detail.submission_limit,
        typeDate: task_detail.submission_typedate ?? 1,
      });
    }

    try {
      const system_config = await setting_all();
      const configs_data: any = JSON.parse(system_config.data[0].configs);

      let submissionTimeConfig: any = null;
      if (task_detail.submission_time) {
        try {
          submissionTimeConfig = JSON.parse(task_detail.submission_time);
        } catch {
          submissionTimeConfig = null;
        }
      }

      const submissionTypeDate: number = task_detail.submission_typedate ?? 1;
      const window = calcSubmissionWindow(
        submissionTypeDate,
        submissionTimeConfig,
        configs_data.time_start_task,
      );

      const data = { task_id: taskId, filter: window ?? undefined };
      const res = await get_getLimitSubmission(data);

      setSubmissionCount(res.result.length);

      if (isTaskAdmin) {
        setDisabledSubmiss(false);
        return;
      }

      if (task_detail.submission_type == 1) {
        if (task_detail.submission_limit <= res.result.length) {
          setDisabledSubmiss(true);
        } else {
          const status = [0, 1, 2];
          const checkProgress =
            res.result &&
            res.result.filter((item: any) => status.includes(item.status));
          if (checkProgress.length != 0) {
            setDisabledSubmiss(true);
          } else {
            setDisabledSubmiss(false);
          }
        }
      } else if (task_detail.submission_type == 2) {
        if (task_detail.submission_limit <= res.result.length) {
          setDisabledSubmiss(true);
        } else {
          setDisabledSubmiss(false);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const submitModelHandler = async () => {
    form.submit();
  };

  const handleFinish = async (value: any) => {
    try {
      const model_split = model.toString().split("+");
      const data =
        subType == 1
          ? {
              task_id: taskId,
              user_id: auth.user.user_id,
              docker_id: model_split[1],
              docker_image: model_split[0],
              datasets: dataset.filter((item: any) =>
                value.dataset.includes(item.value),
              ),
              tag: value.tag || "",
              description: value.description || "",
            }
          : {
              task_id: taskId,
              user_id: auth.user.user_id,
              docker_id: "00000000",
              docker_image: pathUploadFile,
              datasets: [],
              tag: value.tag || "",
              description: value.description || "",
            };
      const res = await submit_model(data);
      if (res.status == "success") {
        setDataFile(null);
        setUploadSuccess(false);
        message.success("Submit successfully!");
        onClose();
        onSubmitSuccess?.();
      } else {
        res.message
          ? message.error(`${res.message}!`)
          : message.error("Submit fail!");
      }
    } catch (error) {
      console.error("Submission Error:", error);
      setDisabledSubmiss(false);
    }
  };

  const saveDraft = async () => {
    try {
      const docker_info = model.split("+");
      const data = {
        task_id: taskId,
        docker_id: docker_info[1] ? docker_info[1] : "",
        docker_image: docker_info[0] ? docker_info[0] : "",
        datasets: dataset.filter((item: any) =>
          form.getFieldValue("dataset")?.includes(item.value),
        ),
      };
      const res = await submit_draft(data);
      if (res.status === "success") {
        onClose();
        message.success("Draft saved successfully!");
      }
    } catch (e) {
      console.error(e);
      message.error("Draft saved Failed!");
    }
  };

  const clearDraft = async () => {
    try {
      const data = { task_id: taskId };
      const res = await submit_draft_delete(data);
      if (res.status === "success") {
        form.resetFields();
        onClose();
        message.success("Clear Draft successfully!");
      }
    } catch (e) {
      console.error(e);
      message.error("Clear Draft failed!");
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success(t("Copied to clipboard!"));
  };

  const propsDragger = {
    name: "file",
    multiple: false,
    showUploadList: false,
    action: "",
    onChange: async () => {},
    onDrop() {},
    beforeUpload: async (file: any) => {
      const expectedType = taskDetail?.submission_file_type;
      if (expectedType) {
        const fileExt = file.name.split(".").pop()?.toLowerCase();
        if (fileExt !== expectedType.toLowerCase()) {
          message.error(t(`File type must be .${expectedType}`));
          return false;
        }
      }

      const expectedName = taskDetail?.submission_file_name;
      const fileExt = file.name.split(".").pop()?.toLowerCase();
      const finalFileName = expectedName
        ? `${expectedName}${fileExt ? `.${fileExt}` : ""}`
        : file.name;
      const uploadFile = expectedName
        ? new File([file], finalFileName, { type: file.type })
        : file;

      setUploadSuccess(false);
      setIsLoading(true);
      const data = {
        task_id: taskId,
        type: uploadFile.type,
        name: finalFileName,
        file: uploadFile,
        user_id: auth.user.user_id,
      };
      try {
        const res = await post_uploadResult(data);
        if (res.status == "success") {
          setDataFile(finalFileName);
          setUploadSuccess(true);
          setPathUploadFile(res.link);
        } else {
          message.error(res.message || t("Upload failed! Please try again."));
          setDataFile(undefined);
          setPathUploadFile("");
        }
      } catch (error) {
        message.error(t("Upload failed! Please try again."));
        setDataFile(undefined);
        setPathUploadFile("");
      } finally {
        setIsLoading(false);
      }
      return false;
    },
  };

  // Render File Submission Guidelines
  const renderFileSubmissionGuidelines = () => (
    <div className="guidelines-content">
      <div className="guideline-section">
        <Title level={5} className="guideline-section-title">
          <SendOutlined /> {t("How to Submit")}
        </Title>
        <div className="simple-steps">
          <div className="step-item">
            <span className="step-number">1</span>
            <div className="step-content">
              {t('Click "Submission" button to start new submission.')}
            </div>
          </div>
          <div className="step-item">
            <span className="step-number">2</span>
            <div className="step-content">
              {t("Upload or drag & drop your file into the box area.")}
              {taskDetail?.submission_tutorial?.includes("filename") && (
                <span className="highlight">
                  {" "}
                  {t("(** please check filename rules)")}
                </span>
              )}
            </div>
          </div>
          <div className="step-item">
            <span className="step-number">3</span>
            <div className="step-content">
              {t('Click "Submit" to confirm the submission.')}
            </div>
          </div>
        </div>
      </div>

      <div className="guideline-section">
        <Title level={5} className="guideline-section-title">
          <HistoryOutlined /> {t("History View")}
        </Title>
        <div className="simple-steps">
          <div className="step-item">
            <span className="step-number">1</span>
            <div className="step-content">
              {t('Click "History" button to view all previous submissions.')}
            </div>
          </div>
          <div className="step-item">
            <span className="step-number">2</span>
            <div className="step-content">
              {t(
                "Click on each row or button in the row to view more details.",
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Model Submission Guidelines
  const renderModelSubmissionGuidelines = () => (
    <Row gutter={[16, 16]} className="model-guidelines-row">
      {/* Step 1: Login */}
      <Col xs={24}>
        <Card
          style={{
            borderRadius: "12px",
            border: "1px solid #e8f4f8",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
          bodyStyle={{ padding: "16px" }}
        >
          <div
            style={{ display: "flex", alignItems: "flex-start", gap: "14px" }}
          >
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #52c41a 0%, #73d13d 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LoginOutlined style={{ color: "#fff", fontSize: "20px" }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "6px",
                }}
              >
                <Tag
                  color="green"
                  style={{ margin: 0, borderRadius: "4px", fontSize: "12px" }}
                >
                  Step 1
                </Tag>
                <Title level={5} style={{ margin: 0, fontSize: "15px" }}>
                  {t("Login")}
                </Title>
              </div>
              <p
                style={{
                  color: "#666",
                  marginBottom: "10px",
                  fontSize: "13px",
                }}
              >
                {t("Dictation")}
              </p>
              <div
                style={{
                  background: "#1e1e1e",
                  borderRadius: "8px",
                  padding: "10px 14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <code
                  style={{
                    color: "#4ec9b0",
                    fontSize: "12px",
                    fontFamily: "'Fira Code', 'Consolas', monospace",
                    wordBreak: "break-all",
                    flex: 1,
                  }}
                >
                  {tutorial.login}
                </code>
                <Tooltip title="Copy">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined style={{ color: "#fff" }} />}
                    onClick={() => copyToClipboard(tutorial.login)}
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      borderRadius: "6px",
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              </div>
              <div
                style={{
                  marginTop: "10px",
                  marginBottom: "10px",
                  padding: "8px 12px",
                  background: "#f6ffed",
                  borderRadius: "6px",
                  border: "1px solid #b7eb8f",
                }}
              >
                <CheckCircleFilled
                  style={{ color: "#52c41a", marginRight: "8px" }}
                />
                <span style={{ fontSize: "12px", color: "#333" }}>
                  {t("Then enter the usercode")} (<strong>{userCode}</strong>){" "}
                  {t("and user password")}
                </span>
              </div>
              <div
                style={{
                  background: "#1e1e1e",
                  borderRadius: "8px",
                  padding: "10px 14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <code
                  style={{
                    color: "#4ec9b0",
                    fontSize: "12px",
                    fontFamily: "'Fira Code', 'Consolas', monospace",
                    wordBreak: "break-all",
                    flex: 1,
                  }}
                >
                  {userCode}
                </code>
                <Tooltip title="Copy">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined style={{ color: "#fff" }} />}
                    onClick={() => copyToClipboard(userCode)}
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      borderRadius: "6px",
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              </div>
            </div>
          </div>
        </Card>
      </Col>

      {/* Step 2: Push Image */}
      <Col xs={24}>
        <Card
          style={{
            borderRadius: "12px",
            border: "1px solid #e8f4f8",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
          bodyStyle={{ padding: "16px" }}
        >
          <div
            style={{ display: "flex", alignItems: "flex-start", gap: "14px" }}
          >
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #1890ff 0%, #69c0ff 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CloudServerOutlined
                style={{ color: "#fff", fontSize: "20px" }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "6px",
                }}
              >
                <Tag
                  color="blue"
                  style={{ margin: 0, borderRadius: "4px", fontSize: "12px" }}
                >
                  Step 2
                </Tag>
                <Title level={5} style={{ margin: 0, fontSize: "15px" }}>
                  {t("Push Image")}
                </Title>
              </div>

              {/* Step 2.1: Tag Image */}
              <p
                style={{
                  color: "#666",
                  marginBottom: "10px",
                  fontSize: "13px",
                }}
              >
                <strong>2.1</strong>{" "}
                {t(
                  "Create the desired image by building or tagging it with the registry name in front",
                )}
              </p>
              <div
                style={{
                  background: "#1e1e1e",
                  borderRadius: "8px",
                  padding: "10px 14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  flexWrap: "wrap",
                  marginBottom: "14px",
                }}
              >
                {editingField.image_tag ? (
                  <input
                    value={editableTutorial.image_tag}
                    onChange={(e) =>
                      setEditableTutorial((prev) => ({
                        ...prev,
                        image_tag: e.target.value,
                      }))
                    }
                    style={{
                      color: "#dcdcaa",
                      fontSize: "12px",
                      fontFamily: "'Fira Code', 'Consolas', monospace",
                      flex: 1,
                      background: "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.25)",
                      borderRadius: "4px",
                      outline: "none",
                      padding: "4px 8px",
                      width: "100%",
                    }}
                    autoFocus
                  />
                ) : (
                  <code
                    style={{
                      color: "#dcdcaa",
                      fontSize: "12px",
                      fontFamily: "'Fira Code', 'Consolas', monospace",
                      wordBreak: "break-all",
                      flex: 1,
                    }}
                  >
                    {editableTutorial.image_tag}
                  </code>
                )}
                <Tooltip title={editingField.image_tag ? "Done" : "Edit"}>
                  <Button
                    type="text"
                    size="small"
                    icon={
                      editingField.image_tag ? (
                        <CheckCircleFilled style={{ color: "#52c41a" }} />
                      ) : (
                        <EditOutlined style={{ color: "#fff" }} />
                      )
                    }
                    onClick={() =>
                      setEditingField((prev) => ({
                        ...prev,
                        image_tag: !prev.image_tag,
                      }))
                    }
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      borderRadius: "6px",
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
                <Tooltip title="Copy">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined style={{ color: "#fff" }} />}
                    onClick={() => copyToClipboard(editableTutorial.image_tag)}
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      borderRadius: "6px",
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              </div>

              {/* Step 2.2: Push Image */}
              <p
                style={{
                  color: "#666",
                  marginBottom: "10px",
                  fontSize: "13px",
                }}
              >
                <strong>2.2</strong> {t("Push image into docker registry")}
              </p>
              <div
                style={{
                  background: "#1e1e1e",
                  borderRadius: "8px",
                  padding: "10px 14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                {editingField.image_push ? (
                  <input
                    value={editableTutorial.image_push}
                    onChange={(e) =>
                      setEditableTutorial((prev) => ({
                        ...prev,
                        image_push: e.target.value,
                      }))
                    }
                    style={{
                      color: "#ce9178",
                      fontSize: "12px",
                      fontFamily: "'Fira Code', 'Consolas', monospace",
                      flex: 1,
                      background: "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.25)",
                      borderRadius: "4px",
                      outline: "none",
                      padding: "4px 8px",
                      width: "100%",
                    }}
                    autoFocus
                  />
                ) : (
                  <code
                    style={{
                      color: "#ce9178",
                      fontSize: "12px",
                      fontFamily: "'Fira Code', 'Consolas', monospace",
                      wordBreak: "break-all",
                      flex: 1,
                    }}
                  >
                    {editableTutorial.image_push}
                  </code>
                )}
                <Tooltip title={editingField.image_push ? "Done" : "Edit"}>
                  <Button
                    type="text"
                    size="small"
                    icon={
                      editingField.image_push ? (
                        <CheckCircleFilled style={{ color: "#52c41a" }} />
                      ) : (
                        <EditOutlined style={{ color: "#fff" }} />
                      )
                    }
                    onClick={() =>
                      setEditingField((prev) => ({
                        ...prev,
                        image_push: !prev.image_push,
                      }))
                    }
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      borderRadius: "6px",
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
                <Tooltip title="Copy">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined style={{ color: "#fff" }} />}
                    onClick={() => copyToClipboard(editableTutorial.image_push)}
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      borderRadius: "6px",
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              </div>
            </div>
          </div>
        </Card>
      </Col>

      {/* Step 3: Logout */}
      <Col xs={24}>
        <Card
          style={{
            borderRadius: "12px",
            border: "1px solid #e8f4f8",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
          bodyStyle={{ padding: "16px" }}
        >
          <div
            style={{ display: "flex", alignItems: "flex-start", gap: "14px" }}
          >
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #ff7875 0%, #ff9c6e 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <LogoutOutlined style={{ color: "#fff", fontSize: "20px" }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "6px",
                }}
              >
                <Tag
                  color="orange"
                  style={{ margin: 0, borderRadius: "4px", fontSize: "12px" }}
                >
                  Step 3
                </Tag>
                <Title level={5} style={{ margin: 0, fontSize: "15px" }}>
                  {t("Logout")}
                </Title>
              </div>
              <p
                style={{
                  color: "#666",
                  marginBottom: "10px",
                  fontSize: "13px",
                }}
              >
                {t("Dictation")}
              </p>
              <div
                style={{
                  background: "#1e1e1e",
                  borderRadius: "8px",
                  padding: "10px 14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <code
                  style={{
                    color: "#c586c0",
                    fontSize: "12px",
                    fontFamily: "'Fira Code', 'Consolas', monospace",
                    wordBreak: "break-all",
                    flex: 1,
                  }}
                >
                  {tutorial.logout}
                </code>
                <Tooltip title="Copy">
                  <Button
                    type="text"
                    size="small"
                    icon={<CopyOutlined style={{ color: "#fff" }} />}
                    onClick={() => copyToClipboard(tutorial.logout)}
                    style={{
                      background: "rgba(255,255,255,0.1)",
                      borderRadius: "6px",
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              </div>
            </div>
          </div>
        </Card>
      </Col>
    </Row>
  );

  return (
    <Modal
      title={
        <div className="modal-header-content">
          <SendOutlined />
          <span> Submission</span>
          {submissionLimitInfo && (
            <span style={{ marginLeft: 12, fontSize: 13, fontWeight: "normal", color: submissionCount >= submissionLimitInfo.limit ? "#ff4d4f" : "#8c8c8c" }}>
              {submissionLimitInfo.limit}{" "}
              {submissionLimitInfo.typeDate === 1
                ? t("per day")
                : submissionLimitInfo.typeDate === 2
                ? t("per week")
                : t("per month")}
            </span>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={isMobile ? "95%" : 1300}
      className={`submission-modal ${isMobile ? "submission-modal-mobile" : ""}`}
      centered
      footer={null}
    >
      <Row gutter={[24, 24]}>
        {/* Left Column - Submission Form */}
        <Col xs={24} md={12}>
          <div className="submission-form-column">
            {/* Scrollable Content Area */}
            <div className="submission-form-content">
              {/* Task Info Header */}
              <div className={`task-info-header ${taskDetail?.submission_type === 1 ? "type-model" : "type-file"}`}>
                <div className="info-row">
                  <span className="info-label">{t("Task Name")}:</span>
                  <span className="info-value">{taskName}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">{t("Submission Type")}:</span>
                  <span
                    className={`submission-type-badge ${
                      taskDetail?.submission_type === 1 ? "model" : "file"
                    }`}
                  >
                    {taskDetail?.submission_type === 1 ? (
                      <>
                        <AppstoreOutlined /> Model
                      </>
                    ) : (
                      <>
                        <CloudUploadOutlined /> File
                      </>
                    )}
                  </span>
                </div>
              </div>

              {/* Form */}
              <Form
                layout="vertical"
                form={form}
                name="form_in_modal"
                initialValues={{ modifier: "public" }}
                onFinish={handleFinish}
                className="submission-form"
              >
                <Form.Item
                  label={
                    <span>
                      <TagOutlined style={{ marginRight: 6 }} />
                      {t("Tag Result")}
                    </span>
                  }
                  name="tag"
                >
                  <Input placeholder={t("Input your tag result")} />
                </Form.Item>

                <Form.Item
                  label={
                    <span>
                      <FileTextOutlined style={{ marginRight: 6 }} />
                      {t("Description")}
                    </span>
                  }
                  name="description"
                >
                  <Input.TextArea
                    placeholder={t("Input your description")}
                    rows={1}
                  />
                </Form.Item>

                {/* Model Selection */}
                {taskDetail?.submission_type == 1 && (
                  <Form.Item
                    name="model"
                    label={
                      <span>
                        <AppstoreOutlined style={{ marginRight: 6 }} />
                        {t("Select Images")}
                      </span>
                    }
                    rules={[
                      { required: true, message: t("Please select a model!") },
                    ]}
                  >
                    <Select
                      placeholder={t("---- Select images ----")}
                      style={{ width: "100%" }}
                      // size="large"
                      value={model}
                      onChange={(value) => setModel(value)}
                      options={selectedModel}
                    />
                  </Form.Item>
                )}

                {/* File Upload */}
                {taskDetail?.submission_type == 2 && (
                  <Form.Item
                    name="file"
                    label={
                      <span>
                        <CloudUploadOutlined style={{ marginRight: 6 }} />
                        {t("Upload File")}
                      </span>
                    }
                    rules={[
                      {
                        validator: () => {
                          if (uploadSuccess && pathUploadFile) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error(t("Please upload a file!")));
                        },
                      },
                    ]}
                  >
                    <div className="upload-area">
                      <Upload.Dragger {...propsDragger}>
                        <Spin spinning={isLoading}>
                          <div style={{ padding: "20px 0" }}>
                            {uploadSuccess ? (
                              <CheckCircleOutlined className="upload-success-icon" />
                            ) : (
                              <CloudUploadOutlined
                                style={{ fontSize: 40, color: "#667eea" }}
                              />
                            )}
                            {uploadSuccess ? (
                              <>
                                <p
                                  className="upload-text"
                                  style={{ color: "#52c41a" }}
                                >
                                  {t("Upload Successful!")}
                                </p>
                                <p className="file-name">{dataFile}</p>
                              </>
                            ) : (
                              <>
                                <p className="upload-text">
                                  {t("Drag your File here, or browse")}
                                </p>
                                <p className="upload-hint">
                                  {t("Support for a single file upload")}
                                </p>
                              </>
                            )}
                          </div>
                        </Spin>
                      </Upload.Dragger>
                    </div>
                  </Form.Item>
                )}

                {/* Dataset Selection */}
                {taskDetail?.submission_type !== 2 &&
                  dataset &&
                  dataset.length > 0 && (
                    <Form.Item
                      label={
                        <span>
                          <AppstoreOutlined style={{ marginRight: 6 }} />
                          {t("Dataset")}
                        </span>
                      }
                      name="dataset"
                    >
                      <Checkbox.Group options={dataset} />
                    </Form.Item>
                  )}
              </Form>
            </div>

            {/* Action Buttons - inside left column */}
            <div className="action-buttons-footer">
              <Button
                className="btn-clear"
                icon={<DeleteOutlined />}
                onClick={() => clearDraft()}
              >
                {t("Clear Draft")}
              </Button>
              <Button
                className="btn-draft"
                icon={<SaveOutlined />}
                onClick={() => saveDraft()}
              >
                {t("Save Draft")}
              </Button>
              <Button
                className="btn-submit"
                type="primary"
                icon={<SendOutlined />}
                onClick={submitModelHandler}
                disabled={disabledSubmiss}
              >
                {t("Submit")}
              </Button>
            </div>
          </div>
        </Col>

        {/* Right Column - Guidelines */}
        <Col xs={24} md={12}>
          <div className="guidelines-column">
            <div className="guidelines-header">
              <InfoCircleOutlined className="guidelines-icon" />
              <Text className="guidelines-title">
                {taskDetail?.submission_type === 2
                  ? t("Submission Guidelines")
                  : t("Docker Registry Guide")}
              </Text>
            </div>

            <div className="guidelines-scroll-area">
              {taskDetail?.submission_type === 2
                ? renderFileSubmissionGuidelines()
                : renderModelSubmissionGuidelines()}
            </div>
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

export default SubmissionModal;
